import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  IconButton,
  Paper,
  TablePagination,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "../../contexts/AuthContext";
import {
  Data,
  PageStateType,
  ReorderTableProps,
} from "../../interfaces/ReorderTable";
import ReorderModal from "../reorderModal";
import { CONFIG } from "../../services/config";
import { FormInputText } from "../form/FormInputText";
import QuickAlert from "../form/quickAlert";

type SortableRowProps = {
  item: Data;
  columnsData: GridColDef[];
  rowHeight: number;
  gridColumns: string | undefined;
  onRowClick?: (item: Data) => void;
};

function SortableRow({
  item,
  columnsData,
  rowHeight,
  gridColumns,
  onRowClick,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getCellTitle = (col: GridColDef, data: any) => {
    const raw = data[col.field];
    return typeof raw === "string" ? raw : "";
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={() => onRowClick && onRowClick(item)}
      sx={{
        alignItems: "center",
        borderBottom: "1px solid rgb(224, 224, 224)",
        height: rowHeight,
        cursor: onRowClick ? "pointer" : "default",
        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        "&:active": { backgroundColor: "rgba(0, 175, 242, 0.08)" },
        display: "grid",
        gridTemplateColumns: gridColumns,
      }}
    >
      {columnsData.map((col) => (
        <Box
          key={col.field}
          sx={{
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            height: rowHeight,
            pl: 2,
          }}
          title={getCellTitle(col, item)}
        >
          <Box
            style={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {col.field === "__drag" ? (
              <IconButton
                size="small"
                sx={{ cursor: "grab", touchAction: "none" }}
                onClick={(event) => event.stopPropagation()}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon />
              </IconButton>
            ) : col.renderCell ? (
              col.renderCell({
                row: item,
                value: item[col.field],
                field: col.field,
                id: item.id,
                formattedValue: item[col.field],
                api: {} as any,
                colDef: col as any,
                isEditable: false,
                hasFocus: false,
                tabIndex: -1,
                rowNode: {} as any,
                cellMode: "view",
              })
            ) : (
              item[col.field]
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function ReorderTable({
  endPoint,
  host,
  messageError,
  rowHeight = 80,
  reorderEndPoint,
  reorderPayload,
  onReorderedItem,
  params1,
  labelParams1,
  cleanButtonStyle,
  cleanButtonType,
  cleanButtonText,
  columnsData,
  onRowClick,
  refreshTable = 0,
  onPageStateChange,
  emptyTableMessage,
  tableWidthMobile,
  gridColumns,
}: ReorderTableProps) {
  const { t } = useTranslation();
  const [searchParams1, setSearchParams1] = useState("");
  const [errorFetching, setErrorFetching] = useState(false);
  const [openReorderModal, setOpenReorderModal] = useState(false);
  const [pageState, setPageState] = useState<PageStateType>({
    isLoading: false,
    data: [],
    total: 0,
    page: 0,
    pageSize: 25,
  });
  const [reorderedItem, setReorderedItem] = useState<{
    item: Data;
    toOrder: number;
  } | null>();

  const [quickAlertProps, setQuickAlertProps] = useState<{
    message: string;
    titleQuickAlert: string;
    severity: AlertColor;
  }>();

  const {
    tokenHeaders,
    country: { countryCode },
    user,
  } = useContext(AuthContext);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (searchParams1) return;

    const oldIndex = pageState.data.findIndex((it) => it.id === active.id);
    const newIndex = pageState.data.findIndex((it) => it.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    if (newIndex > pageState.data.length - 1) return;

    const movedItem = pageState.data[oldIndex];
    const destination = pageState.data[newIndex];
    setReorderedItem({
      item: movedItem,
      toOrder: destination.displayOrder ?? 0,
    });
    onReorderedItem({
      item: movedItem,
      toOrder: destination.displayOrder ?? 0,
    });
    setOpenReorderModal(true);
  };

  const searchWithQueryParams = async () => {
    tokenHeaders.headers.countryCode = countryCode;
    tokenHeaders.headers["Content-Type"] = "application/json";
    axios
      .get(
        `${host || CONFIG.API_URL}${endPoint}page=${pageState.page}&size=${
          pageState.pageSize
        }${params1}${searchParams1}`,
        {
          data: null,
          headers: tokenHeaders.headers,
        },
      )
      .then((response) => {
        setErrorFetching(false);

        setPageState((old) => ({
          ...old,
          isLoading: false,
          data: response.data.content ? response.data.content : response.data,
          total: response.data.totalElements,
        }));
      })
      .catch((error: any) => {
        setErrorFetching(true);

        if (error.response.data.message && messageError) {
          return messageError(error.response.data.message);
        }
      });
  };

  const searchFullData = async () => {
    tokenHeaders.headers["Content-Type"] = "application/json";
    axios
      .get(
        `${host || CONFIG.API_URL}${endPoint}page=${pageState.page}&size=${
          pageState.pageSize
        }`,
        tokenHeaders,
      )
      .then((response) => {
        setErrorFetching(false);

        setPageState((old) => ({
          ...old,
          isLoading: false,
          data: response.data.content ? response.data.content : response.data,
          total: response.data.totalElements,
        }));
      })
      .catch((error: any) => {
        setErrorFetching(true);

        if (error.response.data.message) {
          return messageError?.(error.response.data.message);
        }
      });
  };

  const updateData = async () => {
    setPageState((old) => ({ ...old, isLoading: true }));
    tokenHeaders.headers.email = user.email;
    tokenHeaders.headers.countryCode = countryCode;
    tokenHeaders.headers["Content-Type"] = "application/json";
    axios
      .put(
        `${host || host || CONFIG.API_URL}${reorderEndPoint}${
          reorderedItem?.item.id
        }`,
        reorderPayload,
        tokenHeaders,
      )
      .then(() => {
        setErrorFetching(false);
        searchFullData();
      })
      .catch((error: any) => {
        setErrorFetching(true);

        if (error.response.data.message && messageError) {
          return messageError(error.response.data.message);
        }
      });
  };

  const clearInputs = () => {
    const input1 = document.querySelector("#label1") as HTMLInputElement;

    input1 ? (input1.value = "") : true;

    setSearchParams1("");

    searchFullData();
  };

  const searchData = async () => {
    setPageState((old) => ({ ...old, isLoading: true }));

    !searchParams1 ? (params1 = "") : true;

    searchWithQueryParams();
  };

  useEffect(() => {
    searchFullData();
  }, [refreshTable, pageState.page, pageState.pageSize]);

  useEffect(() => {
    if (onPageStateChange) onPageStateChange(pageState);
  }, [pageState.data]);

  return (
    <>
      <Box>
        {params1 && labelParams1 && (
          <>
            <FormInputText
              id="label1"
              type="text"
              label={labelParams1}
              onChange={(event) => setSearchParams1(event.target.value)}
              onKeyDown={(event: any) => {
                if (event.key === "Enter") {
                  searchData();
                }
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: 2,
                marginBottom: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  searchData();
                }}
              >
                {t("button.Search")}
              </Button>
              <Button
                sx={cleanButtonStyle ? cleanButtonStyle : null}
                variant={cleanButtonType ? cleanButtonType : "contained"}
                onClick={() => {
                  clearInputs();
                }}
              >
                {cleanButtonText || t("button.Reset")}
              </Button>
            </Box>
          </>
        )}

        {errorFetching && (
          <Box sx={{ marginBottom: 2 }}>
            <span style={{ color: "var(--red-100)" }}>
              {searchParams1 ? (
                `${t("alert.notResult")}: ${searchParams1}`
              ) : (
                <>
                  <Alert severity="error">{t("alert.errorFetch")}</Alert>
                  <Alert severity="warning" sx={{ marginY: "1rem" }}>
                    {t("alert.tryAgain")}
                  </Alert>
                </>
              )}
            </span>
          </Box>
        )}
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Paper
          variant="outlined"
          sx={{
            fontSize: 14,
            fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
            border: "1px solid rgb(224, 224, 224)",
            overflowX: "auto",
            width: window.innerWidth > 900 ? "auto" : tableWidthMobile,
          }}
        >
          {/* Cabeçalho */}
          <Box
            sx={{
              minHeight: "4rem",
              alignItems: "center",
              backgroundColor: "var(--gray-100)",
              display: "grid",
              gridTemplateColumns: gridColumns,
            }}
          >
            {columnsData.map((col) => (
              <Box
                key={col.field}
                sx={{
                  pl: 2,
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                }}
              >
                {col.headerName}
              </Box>
            ))}
          </Box>
          {pageState.data.length ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={pageState.data.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                {pageState.data.map((item) => (
                  <SortableRow
                    key={item.id}
                    item={item}
                    columnsData={columnsData}
                    rowHeight={rowHeight}
                    gridColumns={gridColumns}
                    onRowClick={onRowClick}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                padding: 6,
              }}
            >
              {emptyTableMessage || t("banners.table.emptyTable")}
            </Box>
          )}

          <TablePagination
            component="table"
            count={pageState.total}
            page={pageState.page}
            onPageChange={(_, newPage) =>
              setPageState((old) => ({ ...old, page: newPage }))
            }
            rowsPerPage={pageState.pageSize}
            onRowsPerPageChange={(event) => {
              const newPageSize = parseInt(event.target.value, 10);
              setPageState((old) => ({
                ...old,
                pageSize: newPageSize,
                page: 0,
              }));
            }}
            rowsPerPageOptions={[25, 50, 75, 100]}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid rgb(224, 224, 224)",
            }}
          />
        </Paper>
      </Box>
      {openReorderModal && (
        <ReorderModal
          onClose={() => {
            if (reorderPayload) onReorderedItem(null);
            setOpenReorderModal(false);
          }}
          onConfirm={() => {
            updateData().finally(() => {
              if (reorderPayload) onReorderedItem(null);
              setOpenReorderModal(false);
              setPageState((old) => ({ ...old, isLoading: false }));
              setQuickAlertProps({
                message: t("banners.quickAlert.modalCreateBannerSuccess"),
                titleQuickAlert: t("quickAlert.sucess"),
                severity: "success",
              });
            });
          }}
        />
      )}
      <QuickAlert
        visibility={!!quickAlertProps?.message}
        severity={quickAlertProps?.severity}
        title={quickAlertProps?.titleQuickAlert}
        description={quickAlertProps?.message}
        hanhleClose={() => setQuickAlertProps(undefined)}
      />
    </>
  );
}